  /**
 * Session storage manager for auth-critical data (token, active project).
 *
 * Uses sessionStorage (per-tab) instead of localStorage (shared across tabs)
 * to prevent cross-tab session bleed: e.g. admin in Tab 1 overwriting
 * a scheduler session in Tab 2 on refresh.
 *
 * UI preferences (sort fields, view modes, panel widths) remain in
 * localStorage since sharing them across tabs is harmless.
 *
 * Accepts a `storage` parameter so it can be tested with a mock.
 */

/** All session-scoped storage keys used by the application */
const STORAGE_KEYS = {
  TOKEN: 'token',
  ACTIVE_PROJECT_ID: 'activeProjectId',
};

/**
 * Create a storage manager with a given storage backend.
 * In production, uses `window.sessionStorage` (per-tab isolation).
 * In tests, pass a mock.
 *
 * @param {object} storage - Object with getItem, setItem, removeItem
 * @returns {object} Storage manager API
 */
function createStorageManager(storage) {
  return {
    // --- Token ---
    getToken() {
      return storage.getItem(STORAGE_KEYS.TOKEN);
    },
    setToken(token) {
      storage.setItem(STORAGE_KEYS.TOKEN, token);
    },
    clearToken() {
      storage.removeItem(STORAGE_KEYS.TOKEN);
    },

    // --- Active Project ---
    getActiveProjectId() {
      return storage.getItem(STORAGE_KEYS.ACTIVE_PROJECT_ID);
    },
    setActiveProjectId(id) {
      storage.setItem(STORAGE_KEYS.ACTIVE_PROJECT_ID, id);
    },
    clearActiveProjectId() {
      storage.removeItem(STORAGE_KEYS.ACTIVE_PROJECT_ID);
    },

    /**
     * Clear all auth-related storage — used on logout.
     * Must clear both token AND activeProjectId to prevent stale data.
     */
    clearAuthStorage() {
      storage.removeItem(STORAGE_KEYS.TOKEN);
      storage.removeItem(STORAGE_KEYS.ACTIVE_PROJECT_ID);
    },

    /**
     * Clear storage on 401 (session expired).
     * Same as logout — must also clear activeProjectId.
     */
    clear401Storage() {
      storage.removeItem(STORAGE_KEYS.TOKEN);
      storage.removeItem(STORAGE_KEYS.ACTIVE_PROJECT_ID);
    },

    /**
     * Determine if an interceptor should handle a 401 response.
     * @param {object} params
     * @param {number} params.status - HTTP response status
     * @param {string} params.url - Request URL
     * @param {string|undefined} params.authHeader - Authorization header value
     * @returns {boolean}
     */
    shouldHandle401({ status, url, authHeader }) {
      const isAuthEndpoint = url?.includes('/auth/');
      const hasToken = !!authHeader;
      return status === 401 && hasToken && !isAuthEndpoint;
    },
  };
}

/**
 * Default singleton backed by sessionStorage (per-tab isolation).
 * All auth-related reads/writes should go through this instance.
 */
const sessionStorageManager = typeof window !== 'undefined'
  ? createStorageManager(window.sessionStorage)
  : createStorageManager({ getItem: () => null, setItem: () => {}, removeItem: () => {} });

/**
 * One-time cleanup: remove auth keys left in localStorage from before
 * the migration to sessionStorage. Prevents stale tokens from lingering.
 */
function cleanupLegacyLocalStorage() {
  if (typeof window !== 'undefined' && window.localStorage) {
    window.localStorage.removeItem(STORAGE_KEYS.TOKEN);
    window.localStorage.removeItem(STORAGE_KEYS.ACTIVE_PROJECT_ID);
  }
}

export { STORAGE_KEYS, createStorageManager, sessionStorageManager, cleanupLegacyLocalStorage };
